import { useQuery } from "@tanstack/react-query";
import { billingApi } from "@/api/client";

export interface SavingsEvent {
  id: string;
  request_log_id: string;
  methodology_version: string;
  baseline_model: string;
  baseline_input_tokens: number;
  baseline_output_tokens: number;
  baseline_cost: string;
  actual_model: string;
  actual_input_tokens: number;
  actual_output_tokens: number;
  actual_cost: string;
  optimizer_overhead_cost: string;
  gross_savings: string;
  net_savings: string;
  verified_savings: string;
  confidence: string;
  optimizations_applied: Record<string, unknown> | null;
  pricing_snapshot: Record<string, unknown> | null;
  exclusion_reason: string | null;
  created_at: string | null;
}

export interface BillingPeriodSummary {
  id: string;
  tenant_id: string;
  period_start: string;
  period_end: string;
  status: string;
  methodology_version: string;
  total_requests: number;
  billable_requests: number;
  excluded_requests: number;
  total_baseline_cost: string;
  total_actual_cost: string;
  total_verified_savings: string;
  savings_fee_rate: string;
  savings_fee: string;
  total_invoice_amount: string;
  finalized_at: string | null;
  created_at: string | null;
}

export interface BillingPeriodDetail extends BillingPeriodSummary {
  gross_savings: string;
  methodology_doc_url: string;
}

export interface CurrentPeriod {
  period_start: string;
  period_end: string;
  status: string;
  methodology_version: string;
  methodology_doc_url: string;
  savings_fee_rate: string;
  total_requests: number;
  billable_requests: number;
  total_baseline_cost: string;
  total_actual_cost: string;
  total_verified_savings: string;
  projected_savings_fee: string;
  projected_total_invoice: string;
}

export interface EventsPage {
  period_id: string;
  tenant_id: string;
  items: SavingsEvent[];
  next_cursor: string | null;
  limit: number;
}

export function useCurrentPeriod(tenantId: string) {
  return useQuery({
    queryKey: ["billing-v2-current-period", tenantId],
    queryFn: () => billingApi.get("current-period").json<CurrentPeriod>(),
    enabled: !!tenantId,
  });
}

export function usePeriods(tenantId: string, statusFilter?: string) {
  return useQuery({
    queryKey: ["billing-v2-periods", tenantId, statusFilter ?? "all"],
    queryFn: () =>
      billingApi
        .get("periods", {
          searchParams: statusFilter ? { status: statusFilter } : {},
        })
        .json<{ tenant_id: string; periods: BillingPeriodSummary[] }>(),
    enabled: !!tenantId,
  });
}

export function usePeriodDetail(periodId: string | null) {
  return useQuery({
    queryKey: ["billing-v2-period-detail", periodId],
    queryFn: () =>
      billingApi.get(`periods/${periodId}`).json<BillingPeriodDetail>(),
    enabled: !!periodId,
  });
}

export function usePeriodEvents(
  periodId: string | null,
  cursor: string | null,
  limit: number = 50,
) {
  return useQuery({
    queryKey: ["billing-v2-period-events", periodId, cursor, limit],
    queryFn: () => {
      const searchParams: Record<string, string | number> = { limit };
      if (cursor) searchParams.cursor = cursor;
      return billingApi
        .get(`periods/${periodId}/events`, { searchParams })
        .json<EventsPage>();
    },
    enabled: !!periodId,
    placeholderData: (prev) => prev,
  });
}

export function periodExportUrl(periodId: string, format: "csv" | "json"): string {
  return `/api/billing/periods/${periodId}/export.${format}`;
}
